import { Descriptions, Divider, Grid } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type PromoCodeModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/PromoCodeModuleWithPermissionsSpecs';
import { useGetPromoCodeListingQuery } from '../../../../api/queries/getPromoCodeListing';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useListReducer from '../../PromoCodeListing/useListReducer';
import PromoCodeModuleTable from '../ConsentsAndDeclarationsModulePage/PromoCodeModuleTable';
import PromoCodeModuleActions from './PromoCodeModuleActions';
import PromoCodeModuleEmptyList from './PromoCodeModuleEmptyList';

export type PromoCodeModulePageProps = { module: PromoCodeModuleWithPermissionsSpecsFragment };

const PromoCodeModulePage = ({ module }: PromoCodeModulePageProps) => {
    const { t } = useTranslation(['promoCodeModuleDetails']);
    const navigate = useNavigate();

    const { dealerIds } = useMultipleDealerIds();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const [state, dispatch] = useListReducer();
    const { page, pageSize, sort } = state;

    const { data } = useGetPromoCodeListingQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { dealerIds, moduleId: module.id },
        },
    });

    // prepare items as data source
    const dataSource = useMemo(() => (data?.promoCode?.items || []).map(item => ({ ...item, key: item.id })), [data]);

    const total = data?.promoCode?.count || 0;

    const permissions = {
        hasCreatePermission: hasPermissions(module.permissions, [permissionKind.createPromoCode]),
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('promoCodeModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('promoCodeModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<PromoCodeModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('promoCodeModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('promoCodeModuleDetails:dividers.promoCodes')}</Divider>
            {total === 0 ? (
                <PromoCodeModuleEmptyList permissions={permissions} />
            ) : (
                <PromoCodeModuleTable
                    dataSource={dataSource}
                    dispatch={dispatch}
                    module={module}
                    state={state}
                    total={total}
                />
            )}
        </ConsolePageWithHeader>
    );
};

export default PromoCodeModulePage;
